import { useState } from 'react'
import { GripVertical, ChevronDown, ChevronUp, X } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ComponentConfigV2 } from '@/types/configurationV2'
import { COMPONENT_TYPE_INFO } from '@/types/configurationV2'
import { setFirstAttemptOnly } from './componentConfig'
import { WarmupRoundBadges } from './WarmupRoundBadges'

interface ComponentCardProps {
  component: ComponentConfigV2
  onUpdate: (updated: ComponentConfigV2) => void
  onRemove: () => void
  renderOptionsEditor?: (
    component: ComponentConfigV2,
    onUpdate: (updated: ComponentConfigV2) => void
  ) => React.ReactNode
}

export default function ComponentCard({
  component,
  onUpdate,
  onRemove,
  renderOptionsEditor,
}: ComponentCardProps) {
  const [expanded, setExpanded] = useState(false)
  const info = COMPONENT_TYPE_INFO[component.type]

  const handleRemove = () => {
    const componentName = component.label || info.label
    if (window.confirm(`"${componentName}" 컴포넌트를 삭제하시겠습니까?`)) {
      onRemove()
    }
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: component.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-lg ${isDragging ? 'shadow-lg opacity-90' : 'border-gray-200'} ${
        !component.enabled ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-3 p-3">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400">
          <GripVertical className="w-4 h-4" />
        </button>

        <div
          className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ backgroundColor: info.color }}
        >
          {info.icon}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">
            {component.label || info.label}
            <span className="text-xs text-gray-400 font-normal ml-2">{info.label}</span>
          </div>
          {component.type === 'warmup' && <WarmupRoundBadges component={component} />}
        </div>

        <button
          onClick={() => onUpdate({ ...component, enabled: !component.enabled })}
          className={`text-xs px-2 py-0.5 rounded shrink-0 ${
            component.enabled ? 'bg-gray-200 text-gray-700' : 'bg-red-100 text-red-600'
          }`}
        >
          {component.enabled ? 'ON' : 'OFF'}
        </button>

        <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        <button
          type="button"
          onClick={handleRemove}
          className="text-gray-300 hover:text-red-500"
          aria-label="컴포넌트 삭제"
          title="컴포넌트 삭제"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {expanded && renderOptionsEditor && (
        <div className="border-t border-gray-100 p-4">
          <div className="mb-3">
            <label className="text-xs text-gray-500">라벨</label>
            <input
              type="text"
              value={component.label ?? ''}
              onChange={(e) => onUpdate({ ...component, label: e.target.value || undefined })}
              placeholder={info.label}
              className="block w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded"
            />
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-500">라벨 (영문)</label>
            <input
              type="text"
              value={component.labelEn ?? ''}
              onChange={(e) => onUpdate({ ...component, labelEn: e.target.value || undefined })}
              placeholder={info.label}
              className="block w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded"
            />
          </div>
          {/* 생각열기가 명시 회차별 정책을 쓰면 firstAttemptOnly 는 경쟁 설정이라 함께 노출하지 않는다(정책이 우선). */}
          {!(component.type === 'warmup' && component.warmupPolicy) && (
            <div className="mb-3">
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={component.firstAttemptOnly ?? false}
                  onChange={(e) => onUpdate(setFirstAttemptOnly(component, e.target.checked))}
                />
                1회차에만 제공
              </label>
            </div>
          )}
          {renderOptionsEditor(component, onUpdate)}
        </div>
      )}
    </div>
  )
}
